import { addToast } from "@heroui/react";
import { useAppForm, useLanguage } from "@onesolution/hooks";
import type { TranslationsKeys } from "@onesolution/hooks/useLanguage";
import { useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import type { OpenMode } from "./FormDrawer";

export function useLocalStorageForm<T>(opts: {
	localStorageKey: string;
	mode?: OpenMode;
	defaultValues: Partial<T>;
	loadedData?: Partial<T>;
	onSuccess?: () => void;
	onError?: () => void;
	onSubmitMutation: (
		data: { value: T; meta: unknown },
		mutationsOpts: (errorMsg?: string, successMsg?: string) => object,
	) => void;
	formatOnLoad?: (saved: object) => object;
}) {
	const {
		localStorageKey,
		mode,
		defaultValues,
		loadedData,
		onSuccess,
		onError,
		onSubmitMutation,
		formatOnLoad = (out) => out,
	} = opts;

	const { t } = useLanguage();
	const ls = useLocalStorage(
		`${localStorageKey}-${mode || "default"}`,
		defaultValues,
		{
			deserializer: (values) => {
				try {
					const deserialized = JSON.parse(values);

					return formatOnLoad(deserialized);
				} catch (e) {
					console.warn(e);

					return defaultValues;
				}
			},
		},
	);

	const [values, setSavedValues, clearSavedValues] = ls;

	// biome-ignore lint/correctness/useExhaustiveDependencies: <don't want to refresh init value on every local storage change>
	const initValue = useMemo(() => {
		return loadedData ? loadedData : values;
	}, [loadedData]) as T;

	const form = useAppForm({
		defaultValues: { ...initValue, formOpenMode: mode } as T,
		formId: mode as string,
		onSubmit: async (data) => {
			return onSubmitMutation(
				data,
				(errorMsg = t("message.sending-error"), successMsg?: string) => ({
					onError: () => {
						addToast({
							title: t("basic.error"),
							description: errorMsg,
							color: "danger",
						});
						onError?.();
					},
					onSuccess: () => {
						clearSavedValues();
						addToast({
							title: `${t("basic.success")} !`,
							description:
								successMsg ||
								t(`message.toast-${mode}-success` as TranslationsKeys),
							color: "success",
						});
						onSuccess?.();
					},
				}),
			);
		},
		listeners: {
			onChange: ({ fieldApi }) => {
				setSavedValues({
					...form.state.values,
					[fieldApi.name]: fieldApi.state.value,
				});
			},
		},
	});

	Object.assign(form, {
		resetForm: () => {
			form.reset(defaultValues as T);
			setSavedValues(defaultValues);
		},
	});

	return form as typeof form & { resetForm: () => void };
}
